import { useEffect, useRef } from "react";

// Original periodic fragment shader. No remote runtime, fonts, or WebGL library.
// A CSS field remains visible if WebGL is unavailable. Motion stops offscreen,
// in background tabs, and for reduced motion. Resolution is capped for phones.
const vertex = `attribute vec2 position;varying vec2 uv;void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;
const fragment = `precision mediump float;
varying vec2 uv;uniform float phase;uniform vec2 resolution;
float grain(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}
void main(){
 vec2 p=uv; float t=phase;
 float x=p.x*1.4+p.y*.72;
 float warp=.19*sin(p.y*4.8+sin(t)*1.3)+.12*cos(p.x*5.2+cos(t)*1.5);
 float fold=sin((x+warp)*6.8+sin(t)*1.65);
 float fine=sin((x+warp)*12.2+cos(t)*.8+p.y*2.);
 float light=smoothstep(-.85,.7,fold);
 vec3 ivory=vec3(.95,.97,.99),blue=vec3(.17,.43,.70),aqua=vec3(.64,.85,.90),lilac=vec3(.70,.73,.92);
 vec3 c=mix(blue,ivory,light);
 c=mix(c,aqua,.43*smoothstep(.15,.9,sin(p.y*4.1+p.x*3.2+cos(t))));
 c=mix(c,lilac,.25*smoothstep(.3,1.,cos(p.x*5.0-p.y*2.4+sin(t))));
 float rim=pow(max(0.,1.-abs(fold-.12)),12.);
 c+=rim*.12;
 float shadow=pow(max(0.,-fold),3.);c-=shadow*.075;
 c+=fine*.018; c+=(grain(gl_FragCoord.xy)-.5)*.025;
 gl_FragColor=vec4(clamp(c,0.,1.),1.);
}`;
export function LiquidField({
  className = "",
  method = "shader",
}: {
  className?: string;
  method?: "shader" | "css" | "video";
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v || method !== "video") return;
    const media = matchMedia("(prefers-reduced-motion: reduce)");
    let visible = true;
    const sync = () => {
      if (media.matches || document.hidden || !visible) v.pause();
      else
        v.play().catch(() => {
          /* The CSS/poster fallback stays readable when autoplay is blocked. */
        });
    };
    const observer = new IntersectionObserver(
      ([e]) => {
        visible = e.isIntersecting;
        sync();
      },
      { rootMargin: "80px" },
    );
    observer.observe(v);
    document.addEventListener("visibilitychange", sync);
    media.addEventListener("change", sync);
    sync();
    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      media.removeEventListener("change", sync);
      v.pause();
    };
  }, [method]);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || method !== "shader") return;
    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      powerPreference: "low-power",
    });
    if (!gl) return;
    const compile = (type: number, source: string) => {
      const s = gl.createShader(type);
      if (!s) return null;
      gl.shaderSource(s, source);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        gl.deleteShader(s);
        return null;
      }
      return s;
    };
    const vs = compile(gl.VERTEX_SHADER, vertex),
      fs = compile(gl.FRAGMENT_SHADER, fragment),
      program = gl.createProgram();
    if (!vs || !fs || !program) {
      if (vs) gl.deleteShader(vs);
      if (fs) gl.deleteShader(fs);
      if (program) gl.deleteProgram(program);
      return;
    }
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      return;
    }
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW,
    );
    gl.useProgram(program);
    const a = gl.getAttribLocation(program, "position");
    gl.enableVertexAttribArray(a);
    gl.vertexAttribPointer(a, 2, gl.FLOAT, false, 0, 0);
    const phase = gl.getUniformLocation(program, "phase"),
      resolution = gl.getUniformLocation(program, "resolution");
    let frame = 0,
      inView = true,
      previous = 0;
    const started = performance.now();
    const media = matchMedia("(prefers-reduced-motion: reduce)");
    const draw = (now: number) => {
      frame = 0;
      if (now - previous > 30 || media.matches) {
        previous = now;
        gl.uniform1f(
          phase,
          media.matches ? 0.5 : (((now - started) / 18000) % 1) * Math.PI * 2,
        );
        gl.uniform2f(resolution, canvas.width, canvas.height);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
        canvas.dataset.rendered = "true";
      }
      if (inView && !document.hidden && !media.matches)
        frame = requestAnimationFrame(draw);
    };
    const resize = () => {
      const r = canvas.getBoundingClientRect();
      const density = Math.min(devicePixelRatio || 1, 1.3);
      canvas.width = Math.max(1, Math.round(Math.min(1250, r.width * density)));
      canvas.height = Math.max(
        1,
        Math.round(
          (canvas.width * Math.max(1, r.height)) / Math.max(1, r.width),
        ),
      );
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    const run = () => {
      cancelAnimationFrame(frame);
      if (inView && !document.hidden) frame = requestAnimationFrame(draw);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        inView = entry.isIntersecting;
        run();
      },
      { rootMargin: "80px" },
    );
    observer.observe(canvas);
    const size = new ResizeObserver(resize);
    size.observe(canvas);
    document.addEventListener("visibilitychange", run);
    media.addEventListener("change", run);
    resize();
    run();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      size.disconnect();
      document.removeEventListener("visibilitychange", run);
      media.removeEventListener("change", run);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, [method]);
  return (
    <div
      className={"cx-liquid-field cx-field-" + method + " " + className}
      aria-hidden="true"
    >
      {method === "shader" ? (
        <canvas ref={ref} />
      ) : method === "video" ? (
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          preload="metadata"
          poster="/motion-poster.webp"
        >
          <source src="/motion-loop.webm" type="video/webm" />
          <source src="/motion-loop.mp4" type="video/mp4" />
        </video>
      ) : (
        <div className="cx-css-liquid">
          <i />
          <b />
          <span />
        </div>
      )}
    </div>
  );
}
